import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { SalvDataTag, SalvTable, Size } from '@salvapp/ui-components';
import { InternalApiEntityType } from '../../../api-internal';
import { CustomListType } from '../../../custom-lists/customListsReducer';
import { useQueryCustomListSignificantFields } from '../../../custom-lists/significantFields/customListSuiActions';

interface Props {
  nonDjLists: CustomListType[];
  entityType: InternalApiEntityType;
}

interface SummaryRow {
  listId: string;
  listName: string;
  significantFields: string[];
}

const CustomListSuiSummary = ({ nonDjLists, entityType }: Props) => {
  const { data: significantFieldsConfig } = useQueryCustomListSignificantFields(entityType);

  const rows: SummaryRow[] = useMemo(
    () =>
      nonDjLists.map((list) => {
        const fieldIds = new Set(
          significantFieldsConfig?._configuration.find((c) => c.listId === list.id)?.fields ?? []
        );
        const parentFields = (list.schemaFields ?? []).filter((f) => !f.parentId);

        return {
          listId: list.id!,
          listName: list.name!,
          significantFields: parentFields.filter((f) => fieldIds.has(f.id)).map((f) => f.displayName ?? f.fieldName),
        };
      }),
    [nonDjLists, significantFieldsConfig]
  );

  const columnHelper = createColumnHelper<SummaryRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('listName', {
        header: 'List',
        cell: ({ row }) => <Link to={`/custom-lists/${row.original.listId}/settings`}>{row.original.listName}</Link>,
      }),
      columnHelper.accessor('significantFields', {
        header: 'Significant fields',
        cell: ({ getValue }) => {
          const fields = getValue();
          return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {fields.length > 0 ? fields.map((name) => <SalvDataTag key={name} value={name} />) : '—'}
            </div>
          );
        },
      }),
    ],
    []
  );

  return <SalvTable size={Size.SMALL} columns={columns} data={rows} />;
};

export default CustomListSuiSummary;
